/**
 * The character rig (AGENT_ONBOARDING_SPEC.md 3a), copied from the Dashboard:
 * services/dashboard/src/utils/agentCharacters.ts (the same shapes as
 * services/nova/src/utils/agentCharacters.ts and services/nara/src/characters.ts)
 * plus the classic pixel robot from
 * services/dashboard/src/components/domain/agent/AgentSprite.tsx
 * (agentLookColours, STEWARD_BADGE, STEWARD_CAPE, topHat, agentBodyRects, GLOW,
 * HAT_EDGE). Every character sits on the robot's 20x20 grid with the same
 * anchors (head top for hats, shoulders for capes, chest for badges, one eye
 * line). Glass Office has no shared package with the other apps: keep this
 * copy in step with those files. Pure data, no Phaser.
 */

/** An agent's stored look (AgentLook on the API). Null = the classic robot in its role's colour. */
export interface AgentStyle {
  character: string
  color: string | null
  hat: string
  /** True when it wears any cape (older APIs send only this: true is red). */
  cape: boolean
  /** True when it wears any badge (older APIs send only this: true is the star). */
  badge: boolean
  /** The cape: none, red, navy, purple, green or gold (absent on an older API). */
  capeStyle?: string | null
  /** The badge: none, star, heart, bolt, flower or shield (absent on an older API). */
  badgeStyle?: string | null
}

export interface CharacterShape {
  label: string
  /** SVG path on the 20x20 grid; absent for the pixel robot. */
  path?: string
  /** Star points: drawn with a rounded outline so the tips stay soft. */
  rounded?: boolean
  /** A visor face (the cute robot) instead of the shared eyes. */
  visor?: boolean
  /**
   * A light ring round each eye (eyeRings), the eyes drawn dark on it: a thin
   * body (the paper clip) has no room to show white eyes on its own.
   */
  eyeRing?: boolean
  top: number
  chest: [number, number]
  eyesY: number
}

/**
 * The paper clip: a wire 1.4 units thick bent into two nested loops (the
 * outline of the stroke, so it fills like every other character), standing
 * upright with its free ends at the top middle and right.
 */
const CLIP_PATH =
  'M8.2 6.5 L8.2 13 A2.3 2.3 0 0 0 12.8 13 L12.8 5.5 A3.3 3.3 0 0 0 6.2 5.5 L6.2 14 A4.3 4.3 0 0 0 14.8 14 L14.8 7 A0.7 0.7 0 0 0 13.4 7 L13.4 14 A2.9 2.9 0 0 1 7.6 14 L7.6 5.5 A1.9 1.9 0 0 1 11.4 5.5 L11.4 13 A0.9 0.9 0 0 1 9.6 13 L9.6 6.5 A0.7 0.7 0 0 0 8.2 6.5 Z'

export const CHARACTERS: Record<string, CharacterShape> = {
  blob: {
    chest: [14, 14],
    eyesY: 10.5,
    label: 'Blob',
    path: 'M10 4 C15 4 17 8 17 11.5 C17 15.5 14 17 10 17 C6 17 3 15.5 3 11.5 C3 8 5 4 10 4Z',
    top: 4.5,
  },
  // The cute robot from the storyboard: antenna and ball, a rounded body,
  // two legs, and a dark visor with glowing eyes (visorFace).
  bot: {
    chest: [13.5, 12.5],
    eyesY: 10,
    label: 'Robot',
    path: 'M9.5 1.5 L10.5 1.5 L10.5 4.8 L9.5 4.8 Z M10 0.65 A1.1 1.1 0 1 1 9.99 0.65 Z M7 4.5 L13 4.5 Q16 4.5 16 7.5 L16 12 Q16 15 13 15 L7 15 Q4 15 4 12 L4 7.5 Q4 4.5 7 4.5 Z M7.5 14.8 L8.5 14.8 Q9 14.8 9 15.3 L9 17.5 Q9 18 8.5 18 L7.5 18 Q7 18 7 17.5 L7 15.3 Q7 14.8 7.5 14.8 Z M11.5 14.8 L12.5 14.8 Q13 14.8 13 15.3 L13 17.5 Q13 18 12.5 18 L11.5 18 Q11 18 11 17.5 L11 15.3 Q11 14.8 11.5 14.8 Z',
    top: 4.5,
    visor: true,
  },
  cat: {
    chest: [14, 14.5],
    eyesY: 11,
    label: 'Cat',
    path: 'M4.5 7 L5.5 2.5 L8.5 5.5 C9.5 5.3 10.5 5.3 11.5 5.5 L14.5 2.5 L15.5 7 C16.5 8.5 17 10 17 11.5 C17 15 14 17 10 17 C6 17 3 15 3 11.5 C3 10 3.5 8.5 4.5 7Z',
    top: 5.5,
  },
  // Malek, 1 Oct: "a paper clip ... a bit of color around the eyes to stand out".
  clip: {
    chest: [14.1, 12.5],
    eyeRing: true,
    eyesY: 9,
    label: 'Paper clip',
    path: CLIP_PATH,
    top: 3,
  },
  drop: {
    chest: [13.5, 15],
    eyesY: 12.5,
    label: 'Droplet',
    path: 'M10 2.5 C13 7 16 9.5 16 12.5 C16 15.8 13.3 18 10 18 C6.7 18 4 15.8 4 12.5 C4 9.5 7 7 10 2.5Z',
    top: 6,
  },
  ghost: {
    chest: [14, 13],
    eyesY: 9,
    label: 'Ghost',
    path: 'M4 17 L4 9 C4 5.4 6.7 3 10 3 C13.3 3 16 5.4 16 9 L16 17 L14 15.5 L12 17 L10 15.5 L8 17 L6 15.5 Z',
    top: 3.5,
  },
  robot: { chest: [13, 13], eyesY: 10, label: 'Classic robot', top: 5.5 },
  star: {
    chest: [13.5, 14.5],
    eyesY: 11,
    label: 'Star',
    path: 'M10 2.5 L12.2 7.8 L17.75 8.2 L13.5 11.8 L14.85 17.25 L10 14.3 L5.15 17.25 L6.5 11.8 L2.25 8.2 L7.8 7.8 Z',
    rounded: true,
    top: 4,
  },
}

/** The order pickers show: the two robots first (the catalog is sorted). */
export const CHARACTER_ORDER = [
  'robot',
  'bot',
  'blob',
  'star',
  'drop',
  'ghost',
  'cat',
  'clip',
] as const

export const characterOf = (style?: AgentStyle | null): CharacterShape =>
  CHARACTERS[style?.character ?? 'robot'] ?? CHARACTERS.robot

/** One shape on the 20x20 grid (a hat or badge part). Circles centre on x, y. */
export interface RigShape {
  kind: 'path' | 'rect' | 'circle'
  fill: string
  d?: string
  x?: number
  y?: number
  w?: number
  h?: number
  r?: number
  rx?: number
  /** Its own outline (badges); hats take the shared HAT_EDGE instead. */
  stroke?: string
  strokeWidth?: number
}

export type HatShape = RigShape

/** Rounds a computed coordinate so paths never carry float noise. */
const at = (v: number): number => Math.round(v * 100) / 100

/** A hat on the head top `t`, as SVG shapes. Unknown hats draw nothing. */
export const hatShapes = (hat: string, t: number): HatShape[] => {
  switch (hat) {
    case 'cap':
      return [
        {
          d: `M5.5 ${t + 0.5} Q10 ${t - 4.5} 14.5 ${t + 0.5} Z`,
          fill: '#241640',
          kind: 'path',
        },
        {
          fill: '#241640',
          h: 1.3,
          kind: 'rect',
          rx: 0.65,
          w: 5,
          x: 12,
          y: t - 0.5,
        },
      ]
    case 'tophat':
      return [
        {
          fill: '#241640',
          h: 4.5,
          kind: 'rect',
          rx: 0.4,
          w: 6,
          x: 7,
          y: t - 4.5,
        },
        {
          fill: '#241640',
          h: 1.3,
          kind: 'rect',
          rx: 0.65,
          w: 9,
          x: 5.5,
          y: t - 0.5,
        },
        { fill: '#e0457b', h: 0.8, kind: 'rect', w: 6, x: 7, y: t - 1.5 },
      ]
    // The classic HR gold top hat (the pixel robot's crown, highlight, band
    // and brim), as smooth shapes: taller and wider than the dark one.
    case 'goldtop':
      return [
        {
          fill: '#e0a13a',
          h: 5,
          kind: 'rect',
          rx: 0.5,
          w: 8,
          x: 6,
          y: at(t - 5),
        },
        {
          fill: '#f7d074',
          h: 1.1,
          kind: 'rect',
          rx: 0.5,
          w: 8,
          x: 6,
          y: at(t - 5),
        },
        { fill: '#6b4a16', h: 1, kind: 'rect', w: 8, x: 6, y: at(t - 1.9) },
        {
          fill: '#e0a13a',
          h: 1.4,
          kind: 'rect',
          rx: 0.7,
          w: 12,
          x: 4,
          y: at(t - 0.6),
        },
      ]
    case 'beanie':
      return [
        {
          d: `M6 ${t + 0.5} Q10 ${t - 5} 14 ${t + 0.5} Z`,
          fill: '#E0A800',
          kind: 'path',
        },
        {
          fill: '#b38600',
          h: 1.5,
          kind: 'rect',
          rx: 0.75,
          w: 9,
          x: 5.5,
          y: t - 0.5,
        },
        { fill: '#b38600', kind: 'circle', r: 1, x: 10, y: t - 4 },
      ]
    default:
      return []
  }
}

export const CAPE_PATH = 'M5 7.5 L1.5 18.5 L18.5 18.5 L15 7.5 Z'

/** The capes (AgentLook::CAPES on the API) and their colours. */
export const CAPES = ['none', 'red', 'navy', 'purple', 'green', 'gold'] as const

export const CAPE_FILLS: Record<string, string> = {
  gold: '#D4A017',
  green: '#15803D',
  navy: '#1E3A8A',
  purple: '#6B21A8',
  red: '#b4232a',
}

/** The red cape (the only one before styles). */
export const CAPE_FILL = CAPE_FILLS.red

export const CAPE_LABELS: Record<string, string> = {
  gold: 'Gold cape',
  green: 'Green cape',
  navy: 'Navy cape',
  none: 'No cape',
  purple: 'Purple cape',
  red: 'Red cape',
}

/**
 * The cape a look wears: its style, or for an older API (or a style this app
 * does not know) the boolean, true being the red cape.
 */
export const capeOf = (
  style?: { cape?: boolean | null; capeStyle?: string | null } | null,
): string => {
  const named = style?.capeStyle
  if (named === 'none' || (named && CAPE_FILLS[named])) return named
  return style?.cape ? 'red' : 'none'
}

/** The cape's colour, or null for no cape. */
export const capeFill = (
  style?: { cape?: boolean | null; capeStyle?: string | null } | null,
): string | null => CAPE_FILLS[capeOf(style)] ?? null

/** The badges (AgentLook::BADGES on the API). */
export const BADGES = [
  'none',
  'star',
  'heart',
  'bolt',
  'flower',
  'shield',
] as const

export const BADGE_LABELS: Record<string, string> = {
  bolt: 'Bolt',
  flower: 'Flower',
  heart: 'Heart',
  none: 'No badge',
  shield: 'Shield',
  star: 'Star',
}

/**
 * The badge a look wears: its style, or for an older API (or a style this
 * app does not know) the boolean, true being the star.
 */
export const badgeOf = (
  style?: { badge?: boolean | null; badgeStyle?: string | null } | null,
): string => {
  const named = style?.badgeStyle
  if (named === 'none' || (named && BADGE_LABELS[named])) return named
  return style?.badge ? 'star' : 'none'
}

/** A white edge keeps every badge readable on any body colour. */
const BADGE_EDGE = { stroke: '#ffffff', strokeWidth: 0.4 }

/** A badge pinned on the chest (cx, cy), as SVG shapes, about 3.2 units across. */
export const badgeShapes = (
  badge: string,
  [cx, cy]: [number, number],
): RigShape[] => {
  switch (badge) {
    // The gold pin with a white star (the only badge before styles).
    case 'star':
      return [
        { ...BADGE_EDGE, fill: '#E0A800', kind: 'circle', r: 1.6, x: cx, y: cy },
        {
          d: `M${cx} ${at(cy - 0.9)} l0.28 0.6 0.65 0.05 -0.5 0.43 0.15 0.65 -0.58 -0.35 -0.58 0.35 0.15 -0.65 -0.5 -0.43 0.65 -0.05z`,
          fill: '#ffffff',
          kind: 'path',
        },
      ]
    case 'heart':
      return [
        {
          ...BADGE_EDGE,
          d: `M${cx} ${at(cy + 1.5)} c-0.6 -0.45 -1.8 -1.3 -1.8 -2.05 c0 -0.75 0.6 -1.15 1.2 -1.15 c0.35 0 0.6 0.25 0.6 0.6 c0 -0.35 0.25 -0.6 0.6 -0.6 c0.6 0 1.2 0.4 1.2 1.15 c0 0.75 -1.2 1.6 -1.8 2.05z`,
          fill: '#E11D48',
          kind: 'path',
        },
      ]
    case 'bolt':
      return [
        { ...BADGE_EDGE, fill: '#1E3A8A', kind: 'circle', r: 1.6, x: cx, y: cy },
        {
          d: `M${at(cx + 0.35)} ${at(cy - 1.15)} l-1.1 1.35 0.7 0 -0.3 0.95 1.1 -1.35 -0.7 0z`,
          fill: '#FFD75E',
          kind: 'path',
        },
      ]
    // Five petals round a gold centre.
    case 'flower':
      return [
        ...[0, 1, 2, 3, 4].map(
          (i): RigShape => ({
            fill: '#F9A8D4',
            kind: 'circle',
            r: 0.75,
            stroke: '#ffffff',
            strokeWidth: 0.25,
            x: at(cx + 0.95 * Math.cos(((-90 + i * 72) * Math.PI) / 180)),
            y: at(cy + 0.95 * Math.sin(((-90 + i * 72) * Math.PI) / 180)),
          }),
        ),
        { fill: '#FACC15', kind: 'circle', r: 0.6, x: cx, y: cy },
      ]
    case 'shield':
      return [
        {
          ...BADGE_EDGE,
          d: `M${cx} ${at(cy - 1.7)} l1.45 0.55 0 1.2 c0 0.9 -0.65 1.45 -1.45 1.8 c-0.8 -0.35 -1.45 -0.9 -1.45 -1.8 l0 -1.2z`,
          fill: '#2563EB',
          kind: 'path',
        },
        {
          d: `M${at(cx - 0.85)} ${at(cy - 0.45)} l0.85 0.7 0.85 -0.7 0 0.65 -0.85 0.7 -0.85 -0.7z`,
          fill: '#ffffff',
          kind: 'path',
        },
      ]
    default:
      return []
  }
}

/** The dark of the visor and the eyes drawn on an eye ring. */
export const EYE_DARK = '#241640'

/** The ring behind each eye (CharacterShape.eyeRing): a white disc, a dark edge. */
export const EYE_RING = {
  fill: '#ffffff',
  r: 1.8,
  stroke: EYE_DARK,
  strokeWidth: 0.3,
}

/** Where the two eye rings sit: centred on the shared eyes. */
export const eyeRings = (eyesY: number): { x: number; y: number }[] => [
  { x: 8, y: at(eyesY - 0.25) },
  { x: 12, y: at(eyesY - 0.25) },
]

/** The eyes' colour: white on the body, dark on an eye ring. */
export const eyeColour = (shape: CharacterShape): string =>
  shape.eyeRing ? EYE_DARK : '#ffffff'

/**
 * The role's emblem (one reserved corner, automatic, never chosen): EA, PA,
 * HR (manages agents), Steward. The Zunou staff Steward's star is red, so a
 * super admin still reads as one.
 */
export const roleEmblem = (look: {
  ea?: boolean
  personal?: boolean
  hr?: boolean
  steward?: boolean
  stewardStaff?: boolean
}): { text: string; fill: string } | null => {
  if (look.personal) return { fill: '#0f9d8a', text: 'PA' }
  if (look.ea) return { fill: '#D85A30', text: 'EA' }
  if (look.hr) return { fill: '#b38600', text: 'HR' }
  if (look.steward) {
    return { fill: look.stewardStaff ? '#E24B4A' : '#5b21b6', text: '★' }
  }
  return null
}

/** A lighter tint of a colour (the robot's antenna). */
export const tint = (hex: string, amount: number): string => {
  const n = hex.replace('#', '')
  if (n.length !== 6) return hex
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16))
  const mix = (v: number) => Math.round(v + (255 - v) * amount)
  return `#${[r, g, b].map((v) => mix(v).toString(16).padStart(2, '0')).join('')}`
}

/**
 * The palette a look may use (AgentLook::COLORS on the API): the classic
 * indigo, the EA coral and the PA teal (the role bodies, now anyone's pick),
 * then Joseph's palette.
 */
export const STYLE_COLORS = [
  '#5B54C8',
  '#D85A30',
  '#1D9E75',
  '#6D28D9',
  '#A21CAF',
  '#EC4899',
  '#E0A800',
  '#65A30D',
  '#0284C7',
  '#398CFF',
  '#8F86C9',
  '#475569',
  '#6B3A1F',
]

/** A name for each palette colour (the swatches' accessible labels). */
export const COLOR_LABELS: Record<string, string> = {
  '#0284C7': 'Ocean',
  '#1D9E75': 'Teal',
  '#398CFF': 'Sky',
  '#475569': 'Slate',
  '#5B54C8': 'Indigo',
  '#65A30D': 'Green',
  '#6B3A1F': 'Brown',
  '#6D28D9': 'Violet',
  '#8F86C9': 'Lavender',
  '#A21CAF': 'Magenta',
  '#D85A30': 'Coral',
  '#E0A800': 'Gold',
  '#EC4899': 'Pink',
}

export const HATS = ['none', 'cap', 'tophat', 'goldtop', 'beanie'] as const

export const HAT_LABELS: Record<string, string> = {
  beanie: 'Beanie',
  cap: 'Cap',
  goldtop: 'Gold top hat',
  none: 'No hat',
  tophat: 'Top hat',
}

/**
 * The cute robot's face: a dark visor with glowing eyes (open: one bar;
 * shut: a thin line; squint: two slits), on the 20x20 grid.
 */
export const visorFace = (eyesY: number, eyes: 'open' | 'shut' | 'squint') => [
  { fill: '#241640', h: 5, rx: 1.5, w: 9, x: 5.5, y: eyesY - 2.5 },
  ...(eyes === 'shut'
    ? [{ fill: '#9ff0ff', h: 0.7, rx: 0.35, w: 7, x: 6.5, y: eyesY }]
    : eyes === 'squint'
      ? [
          { fill: '#9ff0ff', h: 0.8, rx: 0.4, w: 2.5, x: 6.5, y: eyesY - 0.4 },
          { fill: '#9ff0ff', h: 0.8, rx: 0.4, w: 2.5, x: 11, y: eyesY - 0.4 },
        ]
      : [{ fill: '#9ff0ff', h: 2, rx: 1, w: 7, x: 6.5, y: eyesY - 1 }]),
]

// --- The classic pixel robot (Dashboard AgentSprite.tsx) ---------------------

/** The role flags every robot is drawn with (the Dashboard's AgentLook minus its style). */
export interface AgentRole {
  /** The HR manager (canManageAgents): the gold top hat on the unstyled robot. */
  hr: boolean
  /** A personal agent (runs on its owner's computer): the teal body. */
  personal: boolean
  /** An EA (role kind EA): the coral body. Never together with personal. */
  ea: boolean
  /** Steward on (looks after its principal's account): the ID badge. */
  steward: boolean
  /** The Steward's Zunou staff tier: the red cape. Only with steward. */
  stewardStaff: boolean
}

export interface PixelRect {
  fill: string
  h: number
  w: number
  x: number
  y: number
}

const STANDARD_COLOURS = { antenna: '#c9b8ff', body: '#5b54c8' } as const
const PERSONAL_COLOURS = { antenna: '#9FE1CB', body: '#1D9E75' } as const
const EA_COLOURS = { antenna: '#F5C4B3', body: '#D85A30' } as const

/** Body and antenna colours for the kind: personal teal, EA coral, else indigo. */
export const agentLookColours = ({
  ea = false,
  personal = false,
}: Partial<AgentRole> = {}): { antenna: string; body: string } =>
  personal ? PERSONAL_COLOURS : ea ? EA_COLOURS : STANDARD_COLOURS

/**
 * The Steward's ID badge, drawn over the body: two lanyard straps, the
 * card, its blue stripe and the clip.
 */
export const STEWARD_BADGE: readonly PixelRect[] = [
  { fill: '#F1EFE8', h: 2, w: 1, x: 5, y: 7 },
  { fill: '#F1EFE8', h: 2, w: 1, x: 14, y: 7 },
  { fill: '#F1EFE8', h: 3, w: 6, x: 7, y: 12 },
  { fill: '#378ADD', h: 1, w: 6, x: 7, y: 12 },
  { fill: '#888780', h: 1, w: 2, x: 8, y: 14 },
]

/** The Steward staff tier's red cape, drawn BEHIND the body, and its tips. */
export const STEWARD_CAPE: readonly PixelRect[] = [
  { fill: '#E24B4A', h: 9, w: 2, x: 1, y: 7 },
  { fill: '#E24B4A', h: 9, w: 2, x: 17, y: 7 },
  { fill: '#A32D2D', h: 2, w: 2, x: 0, y: 15 },
  { fill: '#A32D2D', h: 2, w: 2, x: 18, y: 15 },
]

/**
 * The HR manager's gold top hat: the antenna tip pokes through, crown,
 * highlight, band and brim. It replaces the antenna.
 */
const topHat = (antenna: string): PixelRect[] => [
  { fill: antenna, h: 1, w: 2, x: 9, y: 0 },
  { fill: '#e0a13a', h: 4, w: 10, x: 5, y: 1 },
  { fill: '#e0a13a', h: 1, w: 14, x: 3, y: 5 },
  { fill: '#f7d074', h: 1, w: 10, x: 5, y: 1 },
  { fill: '#6b4a16', h: 1, w: 10, x: 5, y: 4 },
]

/**
 * Cape (behind), body, legs, visor, badge and antenna (or hat), in the
 * look's colours. A styled robot passes no role and its own colour.
 */
export const agentBodyRects = (
  {
    ea = false,
    hr = false,
    personal = false,
    steward = false,
    stewardStaff = false,
  }: Partial<AgentRole> = {},
  colour?: string,
): PixelRect[] => {
  // A styled robot wears its chosen colour, its antenna a light tint of it.
  const { antenna, body } = colour
    ? { antenna: tint(colour, 0.55), body: colour }
    : agentLookColours({ ea, personal })
  return [
    ...(steward && stewardStaff ? STEWARD_CAPE : []),
    { fill: body, h: 9, w: 14, x: 3, y: 6 },
    { fill: body, h: 3, w: 2, x: 4, y: 15 },
    { fill: body, h: 3, w: 2, x: 8, y: 15 },
    { fill: body, h: 3, w: 2, x: 12, y: 15 },
    { fill: '#1a1740', h: 3, w: 10, x: 5, y: 9 },
    ...(steward ? STEWARD_BADGE : []),
    ...(hr
      ? topHat(antenna)
      : [
          { fill: antenna, h: 5, w: 2, x: 9, y: 1 },
          { fill: antenna, h: 2, w: 4, x: 8, y: 0 },
        ]),
  ]
}

/** The classic robot's glowing visor line. */
export const GLOW = { fill: '#7ff0ff', h: 1, w: 8, x: 6, y: 10 } as const

/** A light outline keeps a dark hat readable on the dark floor too. */
export const HAT_EDGE = { stroke: '#ffffff', strokeOpacity: 0.55, strokeWidth: 0.35 }
